'use client';

import { useContext, useLayoutEffect } from 'react';
import { QueryClientContext, type QueryClient } from '@tanstack/react-query';
import { registerIdentityScopedSessionClear } from '@/lib/identity-scoped-session-memory';

export type WorkspaceAdmissionScope = { userId: string; orgSlug: string };

type AdmissionEndListener = (ended: WorkspaceAdmissionScope) => void;

const listeners = new Set<AdmissionEndListener>();

/**
 * Subscribe a provider that lives ABOVE the admitted workspace subtree (root
 * modals, viewers, dialogs) to the end of a workspace admission. The keyed
 * subtree drops its own state when it remounts; a provider above it keeps
 * whatever a descendant copied into it (signed URLs, previews), so it must
 * drop that itself when the admission that produced it ends (#4628).
 */
export function onWorkspaceAdmissionEnd(
  listener: AdmissionEndListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * `next/image` with `priority` (and React's `preload()`) hoists
 * `<link rel="preload">` into <head>, and React never removes it. For a signed
 * private URL that keeps a live bearer URL in the document after the page,
 * workspace or account that produced it is gone. Remove such links when an
 * admission or identity ends; they are only fetch hints, so nothing breaks.
 */
const SIGNED_URL_PARAM = /[?&]x-amz-(signature|credential)=/i;

export function purgeSignedPreloadLinks(doc: Document = document): number {
  let removed = 0;
  for (const link of doc.querySelectorAll<HTMLLinkElement>(
    'link[rel="preload"], link[rel="prefetch"]'
  )) {
    // Next emits responsive preloads with `imagesrcset` (and often no
    // `href`); a signed URL in either one keeps the bearer token alive.
    const urls = [link.getAttribute('href'), link.getAttribute('imagesrcset')];
    if (urls.some((url) => url && SIGNED_URL_PARAM.test(url))) {
      link.remove();
      removed += 1;
    }
  }
  return removed;
}

/**
 * Between the end of one admission and the start of the next, any signed
 * preload/prefetch added to <head> belongs to the admission that ended (a
 * late `preload()`, a hint React commits after the purge). Watch <head> in
 * that gap and remove them as they appear; the next admission stops it.
 */
let activeAdmissions = 0;
let gapObserver: MutationObserver | null = null;

function watchHeadBetweenAdmissions(): void {
  if (gapObserver || typeof MutationObserver === 'undefined') return;
  gapObserver = new MutationObserver(() => purgeSignedPreloadLinks());
  gapObserver.observe(document.head, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href', 'imagesrcset'],
  });
}

function stopWatchingHead(): void {
  gapObserver?.disconnect();
  gapObserver = null;
}

if (typeof window !== 'undefined') {
  onWorkspaceAdmissionEnd(() => purgeSignedPreloadLinks());
  registerIdentityScopedSessionClear(() => purgeSignedPreloadLinks());
  // A page restored from the back/forward cache comes back with the <head>
  // it was frozen with; drop signed hints from it before anything reuses them.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) purgeSignedPreloadLinks();
  });
}

/**
 * The shared TanStack QueryClient sits above the keyed admitted subtree, so
 * its entries outlive the admission they were read under. Many of them carry
 * media the server resolved for that admission (signed URLs inside thumbnail
 * lists, file maps, action results), and no allowlist of query names stays
 * complete. So every entry goes when the admission ends, as the identity
 * lifecycle already does on an identity change; whatever the next admission
 * renders is read again, under its own access.
 */
export function clearAdmissionQueryCache(queryClient: QueryClient): void {
  void queryClient.cancelQueries();
  queryClient.removeQueries();
}

/**
 * Rendered inside the admitted, actor+workspace-keyed subtree. Its layout
 * cleanup runs in the same commit that unmounts that subtree (workspace
 * switch, membership loss, sign-out, account switch), before paint, so
 * subscribers clear in that commit too.
 */
export function WorkspaceAdmissionLease({
  userId,
  orgSlug,
}: WorkspaceAdmissionScope): null {
  const queryClient = useContext(QueryClientContext);
  useLayoutEffect(() => {
    activeAdmissions += 1;
    stopWatchingHead();
    return () => {
      activeAdmissions -= 1;
      if (queryClient) clearAdmissionQueryCache(queryClient);
      for (const listener of [...listeners]) listener({ userId, orgSlug });
      if (activeAdmissions === 0) watchHeadBetweenAdmissions();
    };
  }, [queryClient, userId, orgSlug]);
  return null;
}
